import React, { useId, useRef, useState } from "react";
import Select from "react-select";
import "../styles/search-input.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faChevronDown,
  faGlobe,
  faKeyboard,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import Keyboard from "react-simple-keyboard";
import arabic from "simple-keyboard-layouts/build/layouts/arabic";
import "react-simple-keyboard/build/css/index.css";
import useSearchInput from "../custom-hooks/useSearchInput";
import SearchMatchOption from "./SearchMatchOption";

const SearchInput = ({
  searchString,
  setSearchResults,
  setSearchResultSets,
  setCurrentPage,
  setAnalytics,
  setLoading,
  setSearchAfter,
  setTotalCount,
  setEffectiveSearchTerm,
}) => {
  const searchInputId = useId();
  const matchInputId = useId();
  const [showLangDropdown, setShowLangDropdown] = useState(false);
  const langDropdownRef = useRef(null);

  const {
    searchInput,
    setIsHovered,
    keyboardVisibility,
    onChangeInput,
    handleSearch,
    toggleKeyboard,
    handleMatchTypeChange,
    keyboard,
    layoutName,
    onChangeArabicKeyboard,
    onKeyPress,
    Placeholder,
    DropdownIndicator,
    filterOptions,
    defaultValue,
    filterStyles,
    selectedLanguage,
    handleLanguageChange,
    languageOptions,
    enableTranslation,
    handleEnableTranslationChange,
  } = useSearchInput({
    searchString,
    setSearchResults,
    setSearchResultSets,
    setCurrentPage,
    setAnalytics,
    setLoading,
    setSearchAfter,
    setTotalCount,
    setEffectiveSearchTerm,
  });

  const submitSearch = (event) => {
    event.preventDefault();
    handleSearch();
  };

  return (
    <>
      <div className="row g-0">
        <div className="col search-div">
          <form className="search-bar search-bar-labeled" onSubmit={submitSearch}>
            <div className="search-query-control">
              <label className="search-input-label" htmlFor={searchInputId}>Search</label>
              <input
                id={searchInputId}
                value={searchInput}
                className="text-input"
                type="text"
                placeholder={searchString ? searchString : "Search by keyword"}
                onChange={onChangeInput}
              />
            </div>
            <div className="search-match-control">
              <label className="search-match-label" htmlFor={matchInputId}>Match</label>
              <div className="search-match-select">
                <Select
                  inputId={matchInputId}
                  closeMenuOnSelect={true}
                  components={{ Placeholder, DropdownIndicator, Option: SearchMatchOption }}
                  aria-label="Search match type"
                  placeholder={"Choose"}
                  styles={filterStyles}
                  options={filterOptions}
                  blurInputOnSelect={true}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  value={defaultValue}
                  onChange={handleMatchTypeChange}
                />
              </div>
            </div>

            <button
              className="search-submit-button"
              type="submit"
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              aria-label="Search"
            >
              <FontAwesomeIcon className="search-icon" icon={faSearch} />
            </button>
          </form>
        </div>
      </div>
      <div className="row g-0">
        <div className="col search-underbar-row">
          <div className="search-underbar">
            <button
              className="search-hint"
              onClick={toggleKeyboard}
              type="button"
              aria-expanded={keyboardVisibility}
            >
              <FontAwesomeIcon className="search-hint-icon" icon={faKeyboard} />
              <p>Arabic Keyboard</p>
            </button>
            <div className="translation-controls">
              <label className="translation-toggle-label translation-toggle-label--compact">
                <input
                  checked={enableTranslation}
                  className="translation-toggle-checkbox"
                  onChange={handleEnableTranslationChange}
                  type="checkbox"
                />
                <FontAwesomeIcon
                  className="translation-toggle-icon"
                  icon={faGlobe}
                />
                <span className="translation-toggle-switch" aria-hidden="true" />
                <span className="translation-toggle-text">Translate</span>
              </label>
              {enableTranslation && (
                <div className="translate-flow translate-flow--underbar">
                  <span className="translate-flow-label">Translate from</span>
                  <div
                    className="lang-selector"
                    ref={langDropdownRef}
                    onBlur={(e) => {
                      if (!langDropdownRef.current?.contains(e.relatedTarget)) {
                        setShowLangDropdown(false);
                      }
                    }}
                    tabIndex={-1}
                  >
                    <button
                      className="lang-selector-btn"
                      onClick={() => setShowLangDropdown((v) => !v)}
                      type="button"
                      aria-haspopup="listbox"
                      aria-expanded={showLangDropdown}
                    >
                      <span className="lang-selector-short">
                        {selectedLanguage.short}
                      </span>
                      <span className="lang-selector-label">
                        {selectedLanguage.label}
                      </span>
                      <FontAwesomeIcon
                        className="lang-selector-caret"
                        icon={faChevronDown}
                      />
                    </button>
                    {showLangDropdown && (
                      <ul className="lang-dropdown" role="listbox">
                        {languageOptions.map((lang) => (
                          <li
                            key={lang.code}
                            role="option"
                            aria-selected={selectedLanguage.code === lang.code}
                            className={`lang-option${
                              selectedLanguage.code === lang.code
                                ? " lang-option--active"
                                : ""
                            }`}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              handleLanguageChange(lang);
                              setShowLangDropdown(false);
                            }}
                          >
                            <span className="lang-option-short">{lang.short}</span>
                            <span className="lang-option-label">{lang.label}</span>
                            {selectedLanguage.code === lang.code && (
                              <FontAwesomeIcon
                                className="lang-option-check"
                                icon={faCheck}
                              />
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="row arabic-keyboard">
        <div className="col-sm-2"></div>
        <div className="col-sm-8">
          {keyboardVisibility && (
            <Keyboard
              keyboardRef={keyboard.current}
              layoutName={layoutName}
              layout={arabic.layout}
              onChange={onChangeArabicKeyboard}
              onKeyPress={onKeyPress}
            />
          )}
        </div>
        <div className="col-2"></div>
      </div>
    </>
  );
};

export default SearchInput;
